import { useCallback, useEffect, useState } from 'react';
import { Check, CornerUpLeft } from 'lucide-react';
import { Modal } from '../../Modal';
import { Button, ScrollArea, Typography } from '../ui';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import {
  fetchDemoAccounts,
  returnToOwnAccount,
  switchToDemoAccount,
  type DemoAccountsResult,
  type DemoAccountsState,
} from '../../../lib/dev-demo-accounts';
import styles from './DevDemoAccountsPanel.module.css';

// The "Demo accounts" panel behind the brand-mark dev menu — the web port of the native apps' Dev
// Tools ▸ Demo accounts, over the same committed account list. Hop into a demo account, or come back
// to your own dev login. See lib/dev-demo-accounts.ts for why the switch happens in the dev proxy.
// Dev-only, so the copy is not localized (same call as the rest of this folder).

/** Gate #3 (BrandHeader and DevMenu check it too): never renders without a dev build or a grant. */
export function DevDemoAccountsPanel(props: { open: boolean; onClose: () => void }) {
  if (!devToolsUnlocked()) return null;
  return <DemoAccountsPanel {...props} />;
}

function DemoAccountsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [result, setResult] = useState<DemoAccountsResult | undefined>();
  /** The email being switched to (or 'origin' while returning) — the row shows "Switching…". */
  const [busy, setBusy] = useState<string | undefined>();
  const [failure, setFailure] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setResult(undefined);
    setFailure(undefined);
    void fetchDemoAccounts().then((next) => {
      if (!cancelled) setResult(next);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // A session change invalidates every cache in the app (React Query, the module stores, the signed
  // instance) — reload so the whole app repaints as the new account, the same way native pops to Home.
  const run = useCallback(async (key: string, action: () => Promise<DemoAccountsResult>) => {
    setBusy(key);
    setFailure(undefined);
    const next = await action();
    if (next.status === 'ready') {
      window.location.reload();
      return;
    }
    setBusy(undefined);
    setFailure(next.message);
  }, []);

  const state: DemoAccountsState | undefined = result?.status === 'ready' ? result.state : undefined;

  return (
    <Modal open={open} onClose={onClose} width={520} title="Demo accounts">
      <Typography variant="p3" color="secondary">
        Hop into a preset demo account — the same list the native apps use
        (<code>native-apps/demo-accounts.json</code>). Localhost only: the dev proxy signs in and attaches that
        session upstream, so no phone verification and nothing changes in your browser.
      </Typography>

      <ScrollArea className={styles.list}>
        {result === undefined && (
          <Typography variant="p3" color="muted">
            Loading…
          </Typography>
        )}

        {result?.status === 'unavailable' && (
          <Typography variant="p3" color="muted">
            {result.message}
          </Typography>
        )}

        {result?.status === 'error' && (
          <Typography variant="p3" color="muted">
            {result.message}
          </Typography>
        )}

        {state?.active && (
          <button type="button" className={styles.row} data-origin="" onClick={() => run('origin', returnToOwnAccount)}>
            <CornerUpLeft size={14} aria-hidden className={styles.icon} />
            <span className={styles.labels}>
              <span className={styles.label}>Return to {state.originLabel ?? 'my account'}</span>
              <span className={styles.sub}>Your own dev login, from .env.local</span>
            </span>
            {busy === 'origin' && <span className={styles.state}>Switching…</span>}
          </button>
        )}

        {state?.accounts.length === 0 && (
          <Typography variant="p3" color="muted">
            No demo accounts in native-apps/demo-accounts.json.
          </Typography>
        )}

        {state?.accounts.map((account) => {
          const active = state.active?.email === account.email;
          return (
            <button
              key={account.email}
              type="button"
              className={styles.row}
              data-active={active ? '' : undefined}
              disabled={active || busy !== undefined}
              onClick={() => run(account.email, () => switchToDemoAccount(account.email))}
            >
              {active && <Check size={14} aria-hidden className={styles.icon} />}
              <span className={styles.labels}>
                <span className={styles.label}>{account.label}</span>
                <span className={styles.sub}>{account.email}</span>
              </span>
              {busy === account.email ? (
                <span className={styles.state}>Switching…</span>
              ) : active ? (
                <span className={styles.state}>Current</span>
              ) : null}
            </button>
          );
        })}
      </ScrollArea>

      {failure && (
        <Typography variant="p3" color="muted">
          {failure}
        </Typography>
      )}

      <div className={styles.footer}>
        <Typography variant="p3" color="muted">
          Signing in takes a few seconds (the proxy mints a fresh session), then the page reloads.
        </Typography>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
